import React from "react";
import { StarRating } from "./StarRating";
import { Button } from "@/components/ui/button";
import { Loader2, Edit, Plane, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { VerificationBadge } from "@/components/profile/VerificationBadge";
import { ReviewVoteButtons } from "@/components/review/ReviewVoteButtons";
import { useTranslation } from "@/hooks/useTranslation";
import type { Review } from "@/types/review";

interface ReviewContentProps {
  review: Review;
  isCurrentUserReview: boolean;
  upvotes: number;
  downvotes: number;
  isVoting: boolean;
  isDeleting: boolean;
  onUpvote: () => void;
  onDownvote: () => void;
  onDelete: () => void;
  onEdit: () => void;
  showDelete: boolean;
  showEdit: boolean;
}

export const ReviewContent = ({
  review,
  isCurrentUserReview,
  upvotes,
  downvotes,
  isVoting,
  isDeleting,
  onUpvote,
  onDownvote,
  onDelete,
  onEdit,
  showDelete,
  showEdit
}: ReviewContentProps) => {
  const { t } = useTranslation();
  
  const [airportId, setAirportId] = React.useState<number | null>(review.airport_id || null);

  React.useEffect(() => {
    const fetchAirportId = async () => {
      if (!airportId) {
        const {
          data: airports
        } = await supabase.from('airports').select('id').eq('name', review.airport_name).single();
        if (airports?.id) {
          setAirportId(airports.id);
        }
      }
    };
    fetchAirportId();
  }, [review.airport_name, airportId]);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-2">
            {airportId ? (
              <Link to={`/airport/${airportId}`} className="text-2xl font-bold hover:text-blue-600 transition-colors inline-flex items-center gap-2 dark:text-gray-100">
                {t(review.airport_name) || review.airport_name}
                <Plane className="h-5 w-5 text-film-400 dark:text-film-300" />
              </Link>
            ) : (
              <span className="text-2xl font-bold inline-flex items-center gap-2 dark:text-gray-100">
                {t(review.airport_name) || review.airport_name}
                <Plane className="h-5 w-5 text-film-400 dark:text-film-300" />
              </span>
            )}
          </div>
          <p className="text-lg text-film-500 dark:text-film-300">{t(review.airport_location) || review.airport_location}</p>
          {review.profiles && (
            <div className="flex items-center gap-2 mt-2">
              <Link to={`/user/${review.user_id}`} className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300">
                {t('review_by')} {review.profiles.username}
              </Link>
              {review.profiles.verification_status && (
                <VerificationBadge 
                  status={review.profiles.verification_status} 
                  isCreative={review.profiles.is_creative} 
                  className="ml-1" 
                />
              )}
            </div>
          )}
          <p className="text-sm text-film-400 dark:text-film-400/80 mt-1">
            {format(new Date(review.created_at), 'MMMM d, yyyy')}
          </p>
        </div>
        <div className="flex flex-col gap-2 items-end">
          <ReviewVoteButtons 
            reviewId={review.id} 
            initialUpvotes={upvotes} 
            initialDownvotes={downvotes} 
          />
          
          {showEdit && (
            <Button variant="ghost" size="sm" onClick={onEdit} className="h-8">
              <Edit className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between">
        <span className="text-lg font-semibold text-inherit">{t('film_friendly_score')}:</span>
        <StarRating score={review.film_friendly_score} />
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-lg font-semibold text-inherit">{t('hand_check')}:</span>
          <span className={`text-lg font-medium ${review.hand_check_success ? 'text-green-500 dark:text-green-400' : 'text-red-500 dark:text-red-400'}`}>
            {review.hand_check_success ? t('successful') : t('unsuccessful')}
            {review.hand_check_date && review.hand_check_date !== review.created_at && (
              <span className="text-sm text-muted-foreground ml-2">
                ({format(new Date(review.hand_check_date), 'MMMM d, yyyy')})
              </span>
            )}
          </span>
        </div>
        
        {review.terminal && (
          <div className="flex items-center justify-between">
            <span className="text-lg font-semibold text-inherit">{t('Terminal')}:</span>
            <span className="text-lg font-medium">
              {review.terminal}
            </span>
          </div>
        )}
      </div>

      <div className="mt-6">
        <h2 className="text-xl font-semibold mb-4 dark:text-gray-100">{t('review')}</h2>
        <p className="text-film-600 dark:text-gray-300 whitespace-pre-wrap">
          {review.review_text}
        </p>
      </div>

      {showDelete && (
        <div className="pt-4 border-t dark:border-gray-700">
          <Button variant="destructive" onClick={onDelete} disabled={isDeleting} className="w-full">
            {isDeleting ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Trash2 className="h-4 w-4 mr-2" />
            )}
            {t('delete_review')}
          </Button>
        </div>
      )}
    </div>
  );
};
